import { useEffect, type RefObject } from 'react';
import { type ReferenceLineProps } from 'recharts';
// Types
import { ColorTypesEnum } from '@utmb/types/colors';
// Utils
import { getColorStyle } from '@utmb/utils/colors';

// Shared by the UTMB Index evolution charts (runner page and compare page)

// Past this span, only every other year is labelled to avoid crowding
const MAX_LABELLED_YEARS = 15;
// Room kept on the right of today when the chart scrolls (mobile)
const TODAY_RIGHT_BUFFER = 100;

export const AXIS_PROPS = {
    allowDecimals: false,
    interval: 0,
    stroke: getColorStyle(ColorTypesEnum.WHITE),
    strokeWidth: 2,
} as const;

export const AXIS_TICK = { fill: getColorStyle(ColorTypesEnum.WHITE), fontFamily: 'var(--utmb-font-oxanium)', fontWeight: 700 };

/** Convert a date to a fractional year (e.g. 2019-07-02 → 2019.5). */
export function toFractionalYear(date: Date): number {
    const year = date.getFullYear();
    const start = new Date(year, 0, 1).getTime();
    const end = new Date(year + 1, 0, 1).getTime();
    return year + (date.getTime() - start) / (end - start);
}

export interface YearAxis {
    domain: [number, number];
    ticks: number[];
    formatTick: (value: number) => string;
}

/** X axis from the first year to the end of the last one, one tick per year. */
export function getYearAxis(minYear: number, maxYear: number): YearAxis {
    const ticks: number[] = [];
    for (let year = minYear; year <= maxYear; year++) {
        ticks.push(year);
    }

    // Every tick mark is still rendered, only the label is hidden
    const hideEveryOtherLabel = maxYear - minYear + 1 > MAX_LABELLED_YEARS;

    return {
        domain: [minYear, maxYear + 1],
        ticks,
        formatTick: (value: number) => {
            const year = Math.floor(value);
            return hideEveryOtherLabel && (year - minYear) % 2 !== 0 ? '' : String(year);
        },
    };
}

export interface IndexAxis {
    domain: [number, number];
    ticks: number[];
}

/** Y axis snapped to hundreds, with a 50-point margin around the values. */
export function getIndexAxis(values: number[]): IndexAxis {
    // Fallback range when there is no value (e.g. category without any result)
    const minIndex = values.length > 0 ? Math.min(...values) : 500;
    const maxIndex = values.length > 0 ? Math.max(...values) : 900;
    const min = Math.floor((minIndex - 50) / 100) * 100;
    const max = Math.ceil((maxIndex + 50) / 100) * 100;

    const ticks: number[] = [];
    for (let value = min; value <= max; value += 100) {
        ticks.push(value);
    }

    return { domain: [min, max], ticks };
}

/** Dashed vertical line marking today. */
export function getTodayLineProps(
    indexDomain: [number, number],
    label: string
): Pick<ReferenceLineProps, 'segment' | 'stroke' | 'strokeDasharray' | 'strokeWidth' | 'label'> {
    const today = toFractionalYear(new Date());

    return {
        segment: [
            { x: today, y: indexDomain[0] + 2 },
            { x: today, y: indexDomain[1] },
        ],
        stroke: getColorStyle(ColorTypesEnum.WHITE),
        strokeDasharray: '5 5',
        strokeWidth: 4,
        label: { ...AXIS_TICK, value: label, position: 'top', dy: -6, fontSize: 12 },
    };
}

/** When the chart overflows (mobile), scrolls it so that today is in view. */
export function useScrollToToday(chartRef: RefObject<HTMLElement>, yearDomain: [number, number]): void {
    useEffect(() => {
        const element = chartRef.current;
        if (!element) return;

        const [start, end] = yearDomain;
        const span = end - start;
        if (span <= 0) return;

        const todayPixel = ((toFractionalYear(new Date()) - start) / span) * element.scrollWidth;
        element.scrollLeft = Math.max(0, todayPixel - element.clientWidth + TODAY_RIGHT_BUFFER);
    }, [chartRef, yearDomain]);
}
